import {
  renderBulletButton,
  syncBulletCurrent,
} from "../../helpers/paginationBullets";

const DRAG_THRESHOLD = 10;

class ProductCardImageCarousel extends HTMLElement {
  constructor() {
    super();

    this._navigateOnTap();

    const isHoverDevice = window.matchMedia(
      "(hover: hover) and (pointer: fine)",
    ).matches;
    if (isHoverDevice) {
      this.querySelector("[data-primary-slide]")?.remove();
    }

    const slides = this.querySelectorAll(".product-card__hover-carousel-slide");

    if (slides.length < 2) {
      this.querySelectorAll(".product-card__navigation").forEach((el) => {
        el.style.display = "none";
      });
      return;
    }

    window.loadSwiper.then(({ Swiper, Navigation, Pagination, Scrollbar }) => {
      this.swiper = new Swiper(this, {
        modules: [Navigation, Pagination, Scrollbar],
        slidesPerView: "auto",
        cssMode: true,
        loop: true,
        navigation: {
          nextEl: this.querySelector("[data-product-card-navigation-next]"),
          prevEl: this.querySelector("[data-product-card-navigation-previous]"),
        },
        pagination: {
          clickable: true,
          renderBullet: renderBulletButton,
          enabled: true,
          el: ".swiper-pagination",
        },
        scrollbar: {
          el: ".swiper-scrollbar",
          draggable: false,
        },
        on: {
          paginationUpdate: syncBulletCurrent,
        },
      });
    });
  }

  connectedCallback() {}

  disconnectedCallback() {}

  // Touch devices have no overlay link on the images — a tap navigates from
  // here instead, so that a swipe can still scroll the carousel natively. The
  // product title link below the images remains the focusable, crawlable path
  // to the same URL.
  _navigateOnTap() {
    let origin = null;
    let dragged = false;

    const start = (x, y) => {
      origin = { x, y };
      dragged = false;
    };

    const move = (x, y) => {
      if (!origin || dragged) return;

      if (Math.hypot(x - origin.x, y - origin.y) > DRAG_THRESHOLD) {
        dragged = true;
      }
    };

    this.addEventListener("pointerdown", (event) => {
      start(event.clientX, event.clientY);
    });

    this.addEventListener("pointermove", (event) => {
      move(event.clientX, event.clientY);
    });

    // touchmove keeps firing once native scrolling claims the gesture, where
    // pointermove stops. Only movement is trusted as a drag signal: the
    // carousel's own scroll position shifts on gesture start when Swiper
    // corrects a looped slide, which would read as a drag on a stationary tap.
    this.addEventListener(
      "touchstart",
      (event) => {
        const touch = event.touches[0];
        if (touch) start(touch.clientX, touch.clientY);
      },
      { passive: true },
    );

    this.addEventListener(
      "touchmove",
      (event) => {
        const touch = event.touches[0];
        if (touch) move(touch.clientX, touch.clientY);
      },
      { passive: true },
    );

    this.addEventListener("click", (event) => {
      // The overlay link used on hover devices, the navigation arrows, and the
      // pagination and scrollbar controls all handle their own clicks. The
      // pagination container is matched too, so a near-miss between bullets
      // doesn't navigate.
      if (
        event.target.closest("a, button, .swiper-pagination, .swiper-scrollbar")
      ) {
        return;
      }

      // Leave modifier-clicks to the browser rather than forcing a same-tab
      // navigation, for touchscreens that also have a keyboard.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      if (dragged) {
        dragged = false;
        return;
      }

      const url = this.closest("product-card")?.productUrl;
      if (url) window.location.assign(url);
    });
  }
}

if (!customElements.get("product-card-image-carousel")) {
  customElements.define(
    "product-card-image-carousel",
    ProductCardImageCarousel,
  );
}
